import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

const StoryBook = ({ page, formToken, takenChoices = {}, customPaths = [] }) => {
  // Use ref to track initial mount
  const componentMounted = useRef(false);
  const [currentPage, setCurrentPage] = useState(page);
  const [loading, setLoading] = useState(page.status !== 'completed');
  const [error, setError] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(page.audio_url);
  const retryCount = useRef(0);
  const maxRetries = useRef(10);
  const baseDelay = useRef(3000); // Start with 3 seconds

  // Add custom scrollbar styles to the document head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: #f0f9ff;
        border-radius: 10px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #60a5fa;
        border-radius: 10px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #3b82f6;
      }
    `;
    document.head.appendChild(style);

    componentMounted.current = true;

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Make sure we update audioUrl if page.audio_url changes
  useEffect(() => {
    if (page.audio_url) {
      setAudioUrl(page.audio_url);
    }
  }, [page.audio_url]);

  useEffect(() => {
    if (currentPage && currentPage.status !== 'completed') {
      const checkPageStatus = async () => {
        try {
          const response = await axios.get(`/adventure_story_pages/${currentPage.id}/check_status`, {
            headers: {
              'X-CSRF-Token': formToken
            }
          });

          setCurrentPage(response.data);
          // Reset error state and retry count on successful request
          setError(null);
          retryCount.current = 0;

          if (response.data.status === 'completed') {
            setLoading(false);
          } else {
            // Calculate delay with exponential backoff (capped at 15 seconds)
            const delay = Math.min(baseDelay.current * Math.pow(1.5, retryCount.current), 15000);
            setTimeout(checkPageStatus, delay);
          }
        } catch (error) {
          console.error('Error checking page status:', error);
          retryCount.current += 1;

          if (retryCount.current > maxRetries.current) {
            setError('Could not check page status after several attempts. Please refresh the page.');
            setLoading(false);
          } else {
            // Show a temporary error but keep trying
            setError(`Temporary connection issue. Retrying... (${retryCount.current}/${maxRetries.current})`);

            // Calculate delay with exponential backoff (capped at 15 seconds)
            const delay = Math.min(baseDelay.current * Math.pow(1.5, retryCount.current), 15000);
            setTimeout(checkPageStatus, delay);
          }
        }
      };

      checkPageStatus();
    }
  }, [currentPage, formToken]);

  const generateAudio = async (e) => {
    // Prevent default behavior
    if (e) e.preventDefault();

    console.log("Generate audio button clicked");
    setAudioLoading(true);

    try {
      const response = await axios.post(`/adventure_story_pages/${currentPage.id}/generate_audio`, {}, {
        headers: {
          'X-CSRF-Token': formToken
        }
      });

      if (response.data.success) {
        setAudioUrl(response.data.audio_url);
      } else {
        setError('Failed to generate audio. Please try again.');
      }
    } catch (error) {
      console.error('Error generating audio:', error);
      setError('Failed to generate audio. Please try again.');
    } finally {
      setAudioLoading(false);
    }
  };

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-md" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  const handleSubmit = (e, choiceIndex) => {
    e.preventDefault();

    // Create form and submit it
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `/adventure_story_pages/${currentPage.id}/choose_path`;

    // Add CSRF token
    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'authenticity_token';
    csrfInput.value = formToken;
    form.appendChild(csrfInput);

    // Add choice index
    const choiceInput = document.createElement('input');
    choiceInput.type = 'hidden';
    choiceInput.name = 'choice_index';
    choiceInput.value = choiceIndex;
    form.appendChild(choiceInput);

    document.body.appendChild(form);
    form.submit();
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    const customChoice = e.target.elements.custom_choice.value.trim();

    if (!customChoice) return;

    // Create form and submit it
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `/adventure_story_pages/${currentPage.id}/choose_path`;

    // Add CSRF token
    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'authenticity_token';
    csrfInput.value = formToken;
    form.appendChild(csrfInput);

    // Add custom choice
    const choiceInput = document.createElement('input');
    choiceInput.type = 'hidden';
    choiceInput.name = 'custom_choice';
    choiceInput.value = customChoice;
    form.appendChild(choiceInput);

    document.body.appendChild(form);
    form.submit();
  };

  const navigateToPage = (pageId) => {
    window.location.href = `/adventure_story_pages/${pageId}`;
  };

  // Audio component
  const AudioControl = () => {
    if (loading || currentPage.status !== 'completed') {
      return null;
    }

    if (audioLoading) {
      return (
        <div className="flex items-center justify-center mt-4 bg-indigo-100 rounded-full py-2 px-4">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-600 mr-2"></div>
          <span className="text-indigo-700 font-medium">Creating audio...</span>
        </div>
      );
    }

    if (audioUrl) {
      return (
        <div className="mt-4 flex items-center bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl py-3 px-4 shadow-sm">
          <div className="mr-3 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full p-2 shadow-md">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414-7.072m-2.829 9.9a9 9 0 010-12.728" />
            </svg>
          </div>
          <div className="flex-grow">
            <div className="font-medium text-sm text-indigo-800 mb-1">Listen to the story</div>
            <audio
              controls
              src={audioUrl}
              className="w-full h-8"
              preload="auto"
            />
          </div>
        </div>
      );
    }

    return (
      <button
        type="button"
        className="mt-4 flex items-center bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white py-2 px-4 rounded-lg shadow-md transform transition-transform hover:scale-[1.02] duration-200"
        onClick={generateAudio}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414-7.072m-2.829 9.9a9 9 0 010-12.728" />
        </svg>
        <span className="smooth-text">Read to me!</span>
      </button>
    );
  };

  return (
    <div className="w-full mx-auto">
      <div className="flex flex-col md:flex-row bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl shadow-2xl overflow-hidden border border-indigo-100 max-w-screen-2xl mx-auto">
        {/* Left side - Story text */}
        <div className="md:w-1/2 p-6 md:p-8 font-serif relative backdrop-blur-sm bg-white/70">
          <div className="prose prose-lg max-w-none h-[calc(100vh-200px)] overflow-y-auto pr-4 custom-scrollbar">
            {loading && currentPage.status === 'generating_content' ? (
              <div className="flex items-center justify-center h-full w-full">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
                <p className="ml-4 text-lg text-indigo-700 font-medium smooth-text">Writing your story...</p>
              </div>
            ) : (
              <>
                <div
                  className="text-gray-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: currentPage.content.replace(/\n/g, '<br>') }}
                ></div>

                {/* Audio control */}
                <AudioControl />

                {currentPage.choices && currentPage.choices.length > 0 && (
                  <div className="mt-10 bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-xl shadow-sm">
                    <h3 className="text-xl font-bold mb-4 text-indigo-800 smooth-text bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-500">Choose Your Path</h3>
                    <div className="space-y-3">
                      {currentPage.choices.map((choice, index) => {
                        // Check if this choice has already been taken
                        const takenChoice = takenChoices[index];

                        if (takenChoice) {
                          // This is a choice that has already been taken before
                          return (
                            <a
                              key={index}
                              href={`/adventure_story_pages/${takenChoice.id}`}
                              className="block w-full bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white font-medium py-3 px-4 rounded-lg text-left transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-1 smooth-text flex items-center"
                            >
                              <span className="mr-2">✓</span>
                              <span>{choice}</span>
                              <span className="ml-auto text-white/80 text-sm">(Continue this path)</span>
                            </a>
                          );
                        }

                        // This is a choice that hasn't been taken yet
                        return (
                          <button
                            key={index}
                            onClick={(e) => handleSubmit(e, index)}
                            className="w-full bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white font-medium py-3 px-4 rounded-lg text-left transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-1 smooth-text"
                          >
                            {choice}
                          </button>
                        );
                      })}

                      {/* Custom paths section */}
                      {customPaths && customPaths.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-indigo-100">
                          <h4 className="text-md font-semibold mb-3 text-purple-700 smooth-text">
                            Paths created by others:
                          </h4>
                          <div className="space-y-2">
                            {customPaths.map((path, index) => (
                              <a
                                key={`custom-${index}`}
                                href={`/adventure_story_pages/${path.id}`}
                                className="block w-full bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white font-medium py-3 px-4 rounded-lg text-left transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-1 smooth-text flex items-center"
                              >
                                <span>{path.text}</span>
                                {path.user_name && (
                                  <span className="ml-auto text-white/80 text-sm">
                                    (by {path.user_name})
                                  </span>
                                )}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-8 pt-6 border-t border-indigo-100">
                      <h3 className="text-lg font-semibold mb-3 text-indigo-800 smooth-text">Or write your own path:</h3>
                      <form onSubmit={handleCustomSubmit} className="flex">
                        <input
                          type="text"
                          name="custom_choice"
                          placeholder="Write your own adventure path..."
                          className="flex-1 px-4 py-3 border border-indigo-200 rounded-l-lg bg-white bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          required
                        />
                        <button
                          type="submit"
                          className="px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-r-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-colors duration-200 shadow-md smooth-text"
                        >
                          Continue
                        </button>
                      </form>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right side - Image */}
        <div className="md:w-1/2 bg-gradient-to-br from-indigo-500 to-purple-600 p-6 flex items-center justify-center h-[calc(100vh-200px)] overflow-hidden">
          {loading && !currentPage.image_url ? (
            <div className="flex flex-col items-center justify-center h-full w-full">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mb-4"></div>
              <p className="text-lg text-white font-medium smooth-text">
                Drawing your illustration...
              </p>
            </div>
          ) : (
            currentPage.image_url ? (
              <div className="w-full h-full flex items-center justify-center relative group p-2">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-purple-700/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <img
                  src={currentPage.image_url}
                  alt="Story illustration"
                  className="max-h-full max-w-full rounded-xl shadow-lg border-2 border-white/20 object-contain transform transition-transform duration-700 group-hover:scale-[1.02]"
                />
              </div>
            ) : (
              <div className="text-white text-center p-10 w-full h-full flex items-center justify-center">
                <p className="smooth-text text-xl">Illustration will appear here</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

StoryBook.propTypes = {
  page: PropTypes.object.isRequired,
  formToken: PropTypes.string.isRequired,
  takenChoices: PropTypes.object,
  customPaths: PropTypes.array
};

export default StoryBook;